import React, {useState, useEffect, useCallback, useRef} from 'react';
import {AssetManager} from '@dfinity/assets';
import {HttpAgent} from '@dfinity/agent';
import {useAuth} from '../../AuthProvider';
import {Principal} from '@dfinity/principal';

interface AssetManagerPanelProps {
    projectId: string;
    userPrincipal: Principal | null;
    frontendCanisterId: Principal | null;
    onError: (error: string | null) => void;
    onSuccess: (message: string | null) => void;
    editorRef: React.MutableRefObject<any>;
    activeFile: string;
    // NEW: Add callback for when assets change
    onAssetChange?: () => void;
}

interface AssetEntry {
    key: string;
    url: string;
    contentType: string;
    fileName: string;
    size: number;
    isFolder: boolean;
    thumbnailUrl?: string;
}

const AssetManagerPanel: React.FC<AssetManagerPanelProps> = ({
                                                                 projectId,
                                                                 userPrincipal,
                                                                 frontendCanisterId,
                                                                 onError,
                                                                 onSuccess,
                                                                 editorRef,
                                                                 activeFile,
                                                                 onAssetChange // NEW: Accept the callback
                                                             }) => {
    const {identity} = useAuth();
    const [assets, setAssets] = useState<AssetEntry[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedAsset, setSelectedAsset] = useState<AssetEntry | null>(null);
    const [currentFolder, setCurrentFolder] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const [sortBy, setSortBy] = useState<'name' | 'type' | 'date' | 'size'>('name');
    const [isSortAscending, setIsSortAscending] = useState<boolean>(true);
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [hoveredAsset, setHoveredAsset] = useState<AssetEntry | null>(null);
    const [contextMenu, setContextMenu] = useState<{
        visible: boolean,
        x: number,
        y: number,
        asset: AssetEntry | null
    }>({
        visible: false, x: 0, y: 0, asset: null
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const assetManagerRef = useRef<AssetManager | null>(null);

    const [detailsPanelHeight, setDetailsPanelHeight] = useState<number>(300);
    const [isDraggingDivider, setIsDraggingDivider] = useState<boolean>(false);
    const dividerRef = useRef<HTMLDivElement>(null);
    const [showAllAssets, setShowAllAssets] = useState<boolean>(false);
    const [uploadToProjectScope, setUploadToProjectScope] = useState<boolean>(true);


    const host = process.env.DFX_NETWORK === "ic" ? "https://icp0.io" : "http://127.0.0.1:4943";

    const projectPrefix = `projects/${projectId}/`;

    const colorPalette = {
        background: '#fdf6e3',
        foreground: '#5C4033',
        border: '#8B4513',
        highlight: '#f3e5d8',
        accent: '#8B4513',
        error: '#DC2626',
        success: '#16A34A',
        dropActive: 'rgba(139, 69, 19, 0.2)',
        button: '#5C4033',
        buttonText: '#FFF5EE',
        folderIcon: '#FFB900',
        imageIcon: '#4CAF50',
        documentIcon: '#2196F3',
        videoIcon: '#F44336',
        audioIcon: '#9C27B0',
        otherIcon: '#607D8B',
        folderHover: '#ffe4b3'
    };

    useEffect(() => {
        const setupAssetManager = async () => {
            if (!frontendCanisterId || !identity) return;

            try {
                setIsLoading(true);

                const agent = new HttpAgent({
                    identity: await identity,
                    host
                });

                if (host.includes('localhost') || host.includes('127.0.0.1')) {
                    await agent.fetchRootKey();
                }

                const assetManager = new AssetManager({
                    canisterId: frontendCanisterId,
                    agent
                });

                assetManagerRef.current = assetManager;

                // Instead of calling loadAssets directly, trigger a state refresh
                setTimeout(() => {
                    goToProjectRoot();
                }, 0);

            } catch (error) {
                console.error('Error setting up asset manager:', error);
                onError(`Failed to initialize asset manager: ${error instanceof Error ? error.message : String(error)}`);
            } finally {
                setIsLoading(false);
            }
        };

        setupAssetManager();
    }, [frontendCanisterId, identity]);

    useEffect(() => {
        // Only load assets if the asset manager is initialized
        if (assetManagerRef.current) {
            console.log("Loading assets for folder:", currentFolder);
            loadAssets();
        }
    }, [currentFolder]);

    useEffect(() => {
        console.log("Current folder changed to:", currentFolder);
    }, [currentFolder]);

    useEffect(() => {
        debugFolderState();
    }, [currentFolder]);

    useEffect(() => {
        const handleClickOutside = () => {
            closeContextMenu();
        };

        if (contextMenu.visible) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('scroll', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('scroll', handleClickOutside);
            };
        }
    }, [contextMenu.visible]);

    // useEffect(() => {
    //     const handleMouseMove = (e: MouseEvent) => {
    //         if (isDraggingDivider) {
    //             // Calculate the height from the bottom of the window to the mouse position
    //             const containerHeight = window.innerHeight;
    //             const mouseY = e.clientY;
    //             // This will be the height of the details panel (from bottom of window)
    //             const newHeight = containerHeight - mouseY;
    //
    //             // Set reasonable limits for the height (min 100px, max 600px)
    //             if (newHeight >= 100 && newHeight <= 600) {
    //                 setDetailsPanelHeight(newHeight);
    //             }
    //         }
    //     };
    //
    //     const handleMouseUp = () => {
    //         setIsDraggingDivider(false);
    //     };
    //
    //     // Add event listeners when dragging
    //     if (isDraggingDivider) {
    //         document.addEventListener('mousemove', handleMouseMove);
    //         document.addEventListener('mouseup', handleMouseUp);
    //     }
    //
    //     // Cleanup
    //     return () => {
    //         document.removeEventListener('mousemove', handleMouseMove);
    //         document.removeEventListener('mouseup', handleMouseUp);
    //     };
    // }, [isDraggingDivider]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDraggingDivider) {
                // Calculate the height from the bottom of the window to the mouse position
                const containerHeight = window.innerHeight;
                const mouseY = e.clientY;
                // This will be the height of the details panel (from bottom of window)
                const newHeight = containerHeight - mouseY;

                // Set reasonable limits for the height (min 100px, max 600px)
                if (newHeight >= 100 && newHeight <= 600) {
                    setDetailsPanelHeight(newHeight);
                }
            }
        };

        const handleMouseUp = () => {
            setIsDraggingDivider(false);
        };

        // Add event listeners when dragging
        if (isDraggingDivider) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        // Cleanup
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingDivider]);


    const goToProjectRoot = useCallback(() => {
        console.log("Going to project root");
        setCurrentFolder('');
        setSelectedAsset(null);
        // Don't change the showAllAssets state - let user stay in their current mode
        loadAssets();
    }, [showAllAssets]);

    const loadAssets = async () => {
        if (!assetManagerRef.current) return;

        try {
            setIsLoading(true);

            console.log("Current folder before loading assets:", currentFolder);

            // Get list of all assets
            const assetList = await assetManagerRef.current.list();

            console.log("Raw asset list from canister:", assetList);

            // Define project path and root identifiers
            const projectRootPath = `projects/${projectId}`;
            const projectPrefix = `/projects/${projectId}/`;

            // Update root check based on showAllAssets mode
            const isProjectRoot = showAllAssets
                ? currentFolder === ''
                : (currentFolder === '' || currentFolder === projectRootPath);

            console.log("Is at project root:", isProjectRoot);
            console.log("Show all assets mode:", showAllAssets);
            console.log("Looking for assets with prefix:", showAllAssets ? "all" : projectPrefix);

            // Process assets into our structure with folders
            const assetInfos: AssetEntry[] = [];
            const folders = new Set<string>();

            // Check if assetList is an array with the expected structure
            if (!Array.isArray(assetList)) {
                console.error("Asset list is not an array:", assetList);
                throw new Error("Asset list returned from canister is not an array");
            }

            // Filter assets and extract folders
            let filteredAssets = [];

            for (const asset of assetList) {
                let key = "";

                // Handle different potential structures
                if (typeof asset === 'string') {
                    key = asset;
                } else if (asset && typeof asset === 'object') {
                    if ('key' in asset && typeof asset.key === 'string') {
                        key = asset.key;
                    } else {
                        console.log("Asset object doesn't have a string key property:", asset);
                        continue;
                    }
                } else {
                    console.log("Unrecognized asset format:", asset);
                    continue;
                }

                // Calculate relative path based on mode
                const relativePath = showAllAssets
                    ? key.startsWith('/') ? key.substring(1) : key
                    : key.startsWith(projectPrefix) ? key.substring(projectPrefix.length) : null;

                // Skip assets that don't match our current mode
                if (!showAllAssets && relativePath === null) {
                    console.log(`Asset key "${key}" doesn't match prefix "${projectPrefix}"`);
                    continue;
                }

                // NEW: Better handling for "All Assets" mode with current folder
                if (showAllAssets && currentFolder !== '') {
                    // We're in a specific folder in "All Assets" mode
                    const folderPrefix = currentFolder.startsWith('/')
                        ? currentFolder.substring(1)
                        : currentFolder;
                    const expectedPrefix = folderPrefix + '/';

                    // Check if this asset is in the current folder or a subfolder
                    if (!relativePath.startsWith(expectedPrefix)) {
                        console.log(`Asset "${key}" not in current folder "${currentFolder}"`);
                        continue;
                    }

                    // Get the path remainder after the current folder
                    const pathRemainder = relativePath.substring(expectedPrefix.length);
                    const pathParts = pathRemainder.split('/');

                    if (pathParts.length > 1 && pathParts[0]) {
                        // This is in a subfolder - add the folder
                        folders.add(pathParts[0]);
                        console.log(`Added subfolder: ${pathParts[0]}`);
                    } else if (pathParts.length === 1 && pathParts[0]) {
                        // This is a direct file in current folder
                        filteredAssets.push(asset);
                        console.log(`Added file in current folder: ${key}`);
                    }
                } else if (showAllAssets && currentFolder === '') {
                    // At canister root in "All Assets" mode
                    const pathParts = relativePath.split('/');
                    if (pathParts.length > 1 && pathParts[0]) {
                        // This is in a subfolder
                        folders.add(pathParts[0]);
                        console.log(`Added root folder: ${pathParts[0]}`);
                    } else if (pathParts.length === 1 && pathParts[0]) {
                        // This is a root file
                        filteredAssets.push(asset);
                        console.log(`Added root file: ${key}`);
                    }
                } else {
                    // Original project mode logic
                    const isProjectRoot = currentFolder === '' || currentFolder === `projects/${projectId}`;

                    if (isProjectRoot) {
                        const pathParts = relativePath.split('/');
                        if (pathParts.length > 1 && pathParts[0]) {
                            folders.add(pathParts[0]);
                            console.log(`Added folder: ${pathParts[0]}`);
                        } else {
                            filteredAssets.push(asset);
                            console.log(`Added root file: ${key}`);
                        }
                    } else {
                        const subfolderPath = currentFolder.replace(`projects/${projectId}`, '').replace(/^\/+/, '');
                        const expectedPathStart = subfolderPath ? `${subfolderPath}/` : '';

                        console.log(`Checking if asset path "${relativePath}" matches subfolder path "${expectedPathStart}"`);

                        if (!subfolderPath || relativePath.startsWith(expectedPathStart)) {
                            const pathRemainder = subfolderPath
                                ? relativePath.substring(subfolderPath.length + 1)
                                : relativePath;

                            const pathParts = pathRemainder.split('/');

                            if (pathParts.length > 1 && pathParts[0]) {
                                folders.add(pathParts[0]);
                                console.log(`Added subfolder: ${pathParts[0]}`);
                            } else if (!pathRemainder.includes('/') ||
                                (pathRemainder.endsWith('/') && pathRemainder.split('/').length === 2)) {
                                filteredAssets.push(asset);
                                console.log(`Added file in folder: ${key}`);
                            }
                        }
                    }
                }
            }

            console.log("Filtered assets:", filteredAssets);
            console.log("Detected folders:", Array.from(folders));

            // Process assets with careful error handling
            for (const asset of filteredAssets) {
                try {
                    // Extract key based on type
                    const key = typeof asset === 'string' ? asset : (asset.key || "");

                    if (!key) {
                        console.error("Asset key is empty:", asset);
                        continue;
                    }

                    console.log(`Getting details for asset: ${key}`);

                    // Get the asset details
                    const assetDetails = await assetManagerRef.current.get(key);
                    console.log(`Asset details for ${key}:`, assetDetails);

                    if (!assetDetails) {
                        console.error(`No details returned for asset: ${key}`);
                        continue;
                    }

                    const fileName = key.split('/').pop() || '';
                    const contentType = assetDetails.contentType || 'application/octet-stream';

                    let url = '';
                    if (host.includes('localhost') || host.includes('127.0.0.1')) {
                        // Local development environment - using subdomain format
                        url = `http://${frontendCanisterId?.toText()}.localhost:4943${key}`;
                    } else {
                        // Production environment (Internet Computer mainnet)
                        url = `https://${frontendCanisterId?.toText()}.raw.icp0.io${key}`;
                    }

                    // Create thumbnail URL for images
                    const isImage = isImageFile(contentType, fileName);
                    const thumbnailUrl = isImage ? url : undefined;

                    let size = 0;
                    try {
                        // Handle both bigint and number for length
                        if (assetDetails.length !== undefined) {
                            size = typeof assetDetails.length === 'bigint'
                                ? Number(assetDetails.length)
                                : Number(assetDetails.length);
                        }
                    } catch (error) {
                        console.error(`Error getting asset size for ${key}:`, error);
                    }

                    assetInfos.push({
                        key,
                        url,
                        contentType,
                        fileName,
                        size,
                        isFolder: false,
                        thumbnailUrl
                    });
                    console.log(`Added asset to UI: ${fileName}`);
                } catch (error) {
                    const assetKey = typeof asset === 'string' ? asset : (asset.key || "unknown");
                    console.error(`Error processing asset ${assetKey}:`, error);
                }
            }

            // Add folders to assetInfos
            for (const folder of folders) {
                let folderPath;
                if (showAllAssets) {
                    // In all-assets mode, construct path from current folder
                    folderPath = currentFolder ? `${currentFolder}/${folder}` : folder;
                } else {
                    // In project mode, use project prefix
                    folderPath = currentFolder
                        ? `${projectPrefix}${currentFolder.replace(projectRootPath, '').replace(/^\/+/, '')}/${folder}`
                        : `${projectPrefix}${folder}`;
                }

                assetInfos.push({
                    key: folderPath,
                    url: '',
                    contentType: 'folder',
                    fileName: folder,
                    size: 0,
                    isFolder: true
                });
                console.log(`Added folder to UI: ${folder} with path ${folderPath}`);
            }

            // Sort assets
            const sortedAssets = sortAssets(assetInfos);
            console.log("Final sorted assets for UI:", sortedAssets);
            setAssets(sortedAssets);
        } catch (error) {
            console.error('Error loading assets:', error);
            onError(`Failed to load assets: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    const isImageFile = (contentType: string, fileName: string): boolean => {
        // Check by MIME type first
        if (contentType.startsWith('image/')) {
            return true;
        }

        // Check by file extension for cases where MIME type might not be set correctly
        const extension = fileName.toLowerCase().split('.').pop();
        const imageExtensions = ['ico', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'tif'];

        return imageExtensions.includes(extension || '');
    };


    const sortAssets = (assetList: AssetEntry[]) => {
        return [...assetList].sort((a, b) => {
            // Always put folders first
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;

            let comparison = 0;

            switch (sortBy) {
                case 'name':
                    comparison = a.fileName.localeCompare(b.fileName);
                    break;
                case 'type':
                    comparison = a.contentType.localeCompare(b.contentType);
                    break;
                case 'date':
                    // We don't store the date, so default to sorting by name
                    comparison = a.fileName.localeCompare(b.fileName);
                    break;
                case 'size':
                    comparison = a.size - b.size;
                    break;
            }

            return isSortAscending ? comparison : -comparison;
        });
    };

    const uploadAsset = async (file: File) => {
        if (!assetManagerRef.current || !frontendCanisterId) return;

        try {
            setIsLoading(true);

            const contentType = file.type || 'application/octet-stream';

            const fixPath = (path: string): string => {
                // Remove all double slashes
                let result = path;
                while (result.includes("//")) {
                    result = result.replace(/\/\//g, '/');
                }
                // Ensure single leading slash
                if (!result.startsWith('/')) {
                    result = '/' + result;
                }
                return result;
            };

            let filePath = '';

            console.log("=== UPLOAD DEBUG ===");
            console.log("Current folder:", currentFolder);
            console.log("Show all assets mode:", showAllAssets);
            console.log("File name:", file.name);

            // NEW: Check if we're in "all assets" mode for different upload behavior
            if (showAllAssets) {
                // Upload to current location in canister (not project-scoped)
                if (currentFolder === '') {
                    // At root of canister - use leading slash
                    filePath = '/' + file.name;
                    console.log("At canister root, filePath set to:", filePath);
                } else {
                    // In a specific folder - construct path to match asset keys
                    console.log("Building path from currentFolder:", currentFolder);

                    let cleanFolder = currentFolder.replace(/^\/+/, '');
                    console.log("After removing leading slashes:", cleanFolder);

                    // Build path - just use the folder path as-is
                    filePath = cleanFolder + '/' + file.name;
                    console.log("Built filePath (no leading slash yet):", filePath);
                }

                console.log("Built path for All Assets mode (before fixPath):", filePath);
            } else {
                // Original project-scoped upload logic
                filePath = projectPrefix;

                if (currentFolder === '' || currentFolder === `projects/${projectId}`) {
                    // At project root
                } else {
                    let subfolderPath = currentFolder;
                    subfolderPath = subfolderPath.replace(/^\/+/g, '');
                    subfolderPath = subfolderPath.replace(`projects/${projectId}`, '');
                    subfolderPath = subfolderPath.replace(/^\/+/g, '');

                    if (subfolderPath) {
                        filePath += subfolderPath;
                        if (!filePath.endsWith('/')) {
                            filePath += '/';
                        }
                    }
                }

                filePath += file.name;
            }

            console.log("Before fixPath:", filePath);

            // DON'T use fixPath - it adds a leading slash but AssetManager adds one too
            // Just ensure no double slashes within the path
            while (filePath.includes("//")) {
                filePath = filePath.replace(/\/\//g, '/');
            }
            // Remove any leading slash - AssetManager will add it
            filePath = filePath.replace(/^\/+/, '');

            console.log("Final upload path (for AssetManager API):", filePath);
            console.log("===================");

            const fileContent = new Uint8Array(await file.arrayBuffer());

            await assetManagerRef.current.store(fileContent, {
                fileName: filePath,
                contentType
            });

            onSuccess(`File ${file.name} uploaded successfully to ${showAllAssets ? 'canister' : 'project'}`);

            await loadAssets();
            onAssetChange?.();
        } catch (error) {
            console.error('Error uploading asset:', error);
            onError(`Failed to upload asset: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteAsset = async (asset: AssetEntry) => {
        if (!assetManagerRef.current) return;

        try {
            setIsLoading(true);

            if (asset.isFolder) {
                // Get all assets
                const assetList = await assetManagerRef.current.list();

                // Delete all assets in this folder
                const folderPath = asset.key;

                if (Array.isArray(assetList)) {
                    for (const assetItem of assetList) {
                        const key = assetItem.key;

                        // Check if this asset is in the folder
                        if (key.startsWith(folderPath)) {
                            await assetManagerRef.current.delete(key);
                        }
                    }
                }
            } else {
                // Delete single asset
                await assetManagerRef.current.delete(asset.key);
            }

            // If the deleted asset was selected, clear selection
            if (selectedAsset && selectedAsset.key === asset.key) {
                setSelectedAsset(null);
            }

            onSuccess(`${asset.isFolder ? 'Folder' : 'File'} "${asset.fileName}" deleted successfully`);

            // Reload assets
            await loadAssets();

            // NEW: Trigger sidebar refresh
            onAssetChange?.();
        } catch (error) {
            console.error('Error deleting asset:', error);
            onError(`Failed to delete asset: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    const createFolder = async (folderName: string) => {
        if (!assetManagerRef.current) return;

        try {
            setIsLoading(true);

            let folderPath;

            // Check if we're in "all assets" mode
            if (showAllAssets) {
                // Create folder at current location in canister (not project-scoped)
                if (currentFolder === '') {
                    // At root - don't add leading slash, AssetManager will add it
                    folderPath = folderName;
                } else {
                    // In a subfolder - remove any leading slash from currentFolder
                    const cleanFolder = currentFolder.replace(/^\/+/, '');
                    folderPath = `${cleanFolder}/${folderName}`;
                }
            } else {
                // Original project-scoped folder creation logic
                if (currentFolder === '') {
                    folderPath = `${projectPrefix}${folderName}`;
                } else if (currentFolder === `projects/${projectId}`) {
                    folderPath = `${projectPrefix}${folderName}`;
                } else if (currentFolder.startsWith('projects/')) {
                    const relativePath = currentFolder.replace(`projects/${projectId}`, '').replace(/^\/+/, '');
                    folderPath = `${projectPrefix}${relativePath ? relativePath + '/' : ''}${folderName}`;
                } else {
                    folderPath = `${projectPrefix}${currentFolder}/${folderName}`;
                }
            }

            // Remove any leading slash - AssetManager will add it
            folderPath = folderPath.replace(/^\/+/, '');

            // Remove any double slashes within the path
            while (folderPath.includes("//")) {
                folderPath = folderPath.replace(/\/\//g, '/');
            }

            console.log("Creating folder at path:", folderPath);

            await assetManagerRef.current.store(new Uint8Array(0), {
                fileName: `${folderPath}/.keep`,
                contentType: 'application/octet-stream'
            });

            onSuccess(`Folder "${folderName}" created successfully in ${showAllAssets ? 'canister' : 'project'}`);

            await loadAssets();
            onAssetChange?.();
        } catch (error) {
            console.error('Error creating folder:', error);
            onError(`Failed to create folder: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    const isAtProjectRoot = () => {
        const projectRootPath = `projects/${projectId}`;
        const result = currentFolder === '' || currentFolder === projectRootPath;
        console.log(`isAtProjectRoot check: ${result} (currentFolder="${currentFolder}", projectRootPath="${projectRootPath}")`);
        return result;
    };

    const insertAssetUrlIntoEditor = () => {
        if (!selectedAsset || !editorRef.current || selectedAsset.isFolder) return;

        const editor = editorRef.current;
        const position = editor.getPosition();

        if (!position) return;

        // Get file extension to determine appropriate insertion format
        const fileExt = activeFile.split('.').pop()?.toLowerCase();
        let insertText = selectedAsset.url;

        if (fileExt === 'html' || fileExt === 'jsx' || fileExt === 'tsx') {
            // For HTML/JSX files, use appropriate tag based on content type
            if (selectedAsset.contentType.startsWith('image/')) {
                insertText = `<img src="${selectedAsset.url}" alt="${selectedAsset.fileName}" />`;
            } else if (selectedAsset.contentType.startsWith('video/')) {
                insertText = `<video controls>\n  <source src="${selectedAsset.url}" type="${selectedAsset.contentType}" />\n  Your browser does not support the video tag.\n</video>`;
            } else if (selectedAsset.contentType.startsWith('audio/')) {
                insertText = `<audio controls>\n  <source src="${selectedAsset.url}" type="${selectedAsset.contentType}" />\n  Your browser does not support the audio tag.\n</audio>`;
            } else {
                insertText = `<a href="${selectedAsset.url}">${selectedAsset.fileName}</a>`;
            }
        } else if (fileExt === 'css') {
            // For CSS files, use url() format
            insertText = `url("${selectedAsset.url}")`;
        } else if (fileExt === 'mo') {
            // For Motoko, use proper string format
            insertText = `"${selectedAsset.url}"`;
        }

        // Insert at cursor position
        editor.executeEdits('asset-insertion', [{
            range: {
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column
            },
            text: insertText
        }]);

        onSuccess(`Inserted asset URL into editor`);
    };

    const copyAssetUrl = () => {
        if (!selectedAsset || selectedAsset.isFolder) return;

        navigator.clipboard.writeText(selectedAsset.url)
            .then(() => onSuccess(`Asset URL copied to clipboard`))
            .catch(error => onError(`Failed to copy URL: ${error instanceof Error ? error.message : String(error)}`));
    };

    const navigateToFolder = (asset: AssetEntry) => {
        if (!asset.isFolder) return;

        // Get the folder name directly from the asset
        const folderName = asset.fileName;

        // Create a path structure based on current location and mode
        let newPath;

        if (showAllAssets) {
            // In all-assets mode, just append to current path
            if (currentFolder === '') {
                newPath = folderName;
            } else {
                newPath = `${currentFolder}/${folderName}`;
            }
        } else {
            // In project mode, maintain project prefix
            if (currentFolder === '') {
                // At empty root
                newPath = `projects/${projectId}/${folderName}`;
            } else if (currentFolder === `projects/${projectId}`) {
                // At project root
                newPath = `projects/${projectId}/${folderName}`;
            } else {
                // In any other folder
                newPath = `${currentFolder}/${folderName}`;
            }
        }

        console.log(`Navigating to folder: ${folderName}`);
        console.log(`New path will be: ${newPath}`);

        // Set the new folder path and clear selection
        setCurrentFolder(newPath);
        setSelectedAsset(null);
    };

    const debugFolderState = () => {
        const projectRootPath = `projects/${projectId}`;
        console.log({
            currentFolder,
            isAtRoot: currentFolder === '',
            isAtProjectRoot: currentFolder === projectRootPath,
            projectRootPath,
            checkIsAtProjectRoot: isAtProjectRoot()
        });
    };

    const navigateUp = () => {
        // Can't go up if at root
        if (currentFolder === '') return;

        // Split the path and remove the last segment
        const folderParts = currentFolder.split('/');
        folderParts.pop();

        // Join the path back together or set to empty if at root
        let newPath;

        if (showAllAssets) {
            // In all-assets mode, simple path navigation
            newPath = folderParts.length > 0 ? folderParts.join('/') : '';
        } else {
            // In project mode, handle project root properly
            if (folderParts.length <= 2 && folderParts[0] === 'projects' && folderParts[1] === projectId) {
                // If we're going up from projects/projectId/something to projects/projectId
                // Set to empty string to represent project root
                newPath = '';
            } else {
                newPath = folderParts.length > 0 ? folderParts.join('/') : '';
            }
        }

        console.log(`Navigating up from ${currentFolder} to ${newPath}`);

        // Just update the state - useEffect will handle loading assets
        setCurrentFolder(newPath);
        setSelectedAsset(null);
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            // Handle files
            Array.from(e.dataTransfer.files).forEach(file => {
                uploadAsset(file);
            });
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            Array.from(e.target.files).forEach(file => {
                uploadAsset(file);
            });
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleAssetClick = (asset: AssetEntry) => {
        // Select both files and folders for toolbar actions
        setSelectedAsset(asset);
        // Close context menu if it's open
        setContextMenu({visible: false, x: 0, y: 0, asset: null});
    };

    const handleAssetDoubleClick = (asset: AssetEntry) => {
        if (asset.isFolder) {
            navigateToFolder(asset);
        } else {
            window.open(asset.url, '_blank');
        }
    };

    const handleContextMenu = (e: React.MouseEvent, asset: AssetEntry) => {
        e.preventDefault();
        if (asset.isFolder) {
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                asset: asset
            });
        }
    };

    const closeContextMenu = () => {
        setContextMenu({visible: false, x: 0, y: 0, asset: null});
    };

    const filteredAssets = assets.filter(asset =>
        asset.fileName.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !asset.fileName.endsWith('.keep') // Hide .keep files from display
    );

    const renderFileIcon = (asset: AssetEntry) => {
        if (asset.isFolder) {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorPalette.folderIcon}
                     strokeWidth="2">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                </svg>
            );
        }

        const contentType = asset.contentType;

        if (isImageFile(contentType, asset.fileName)) {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorPalette.imageIcon}
                     strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                </svg>
            );
        } else if (contentType.startsWith('video/')) {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorPalette.videoIcon}
                     strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                    <line x1="7" y1="2" x2="7" y2="22"/>
                    <line x1="17" y1="2" x2="17" y2="22"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <line x1="2" y1="7" x2="7" y2="7"/>
                    <line x1="2" y1="17" x2="7" y2="17"/>
                    <line x1="17" y1="17" x2="22" y2="17"/>
                    <line x1="17" y1="7" x2="22" y2="7"/>
                </svg>
            );
        } else if (contentType.startsWith('audio/')) {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorPalette.audioIcon}
                     strokeWidth="2">
                    <path d="M9 18V5l12-2v13"/>
                    <circle cx="6" cy="18" r="3"/>
                    <circle cx="18" cy="16" r="3"/>
                </svg>
            );
        } else {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorPalette.documentIcon}
                     strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                </svg>
            );
        }
    };

    const renderAssetPreview = () => {
        if (!selectedAsset || selectedAsset.isFolder) return null;

        const contentType = selectedAsset.contentType;

        // Use the helper function to determine if it's an image (including .ico files)
        if (isImageFile(contentType, selectedAsset.fileName)) {
            return (
                <img
                    src={selectedAsset.url}
                    alt={selectedAsset.fileName}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        marginBottom: '10px'
                    }}
                />
            );
        } else if (contentType.startsWith('video/')) {
            return (
                <video
                    controls
                    style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        marginBottom: '10px'
                    }}
                >
                    <source src={selectedAsset.url} type={contentType}/>
                    Your browser does not support the video tag.
                </video>
            );
        } else if (contentType.startsWith('audio/')) {
            return (
                <audio
                    controls
                    style={{
                        width: '100%',
                        marginBottom: '10px'
                    }}
                >
                    <source src={selectedAsset.url} type={contentType}/>
                    Your browser does not support the audio tag.
                </audio>
            );
        } else if (contentType === 'application/pdf') {
            return (
                <iframe
                    src={selectedAsset.url}
                    style={{
                        width: '100%',
                        height: '300px',
                        border: 'none',
                        marginBottom: '10px'
                    }}
                    title={selectedAsset.fileName}
                />
            );
        } else {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    height: '200px',
                    backgroundColor: colorPalette.highlight,
                    borderRadius: '4px',
                    marginBottom: '10px'
                }}>
                    {renderFileIcon(selectedAsset)}
                    <p style={{marginTop: '8px'}}>{selectedAsset.fileName}</p>
                    <a
                        href={selectedAsset.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block',
                            padding: '6px 12px',
                            backgroundColor: colorPalette.button,
                            color: colorPalette.buttonText,
                            borderRadius: '4px',
                            textDecoration: 'none',
                            marginTop: '8px',
                            fontSize: '14px'
                        }}
                    >
                        Open File
                    </a>
                </div>
            );
        }
    };

    if (!frontendCanisterId) {
        return (
            <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: '20px',
                backgroundColor: colorPalette.background,
                color: colorPalette.foreground
            }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colorPalette.foreground}
                     strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                </svg>
                <h3 style={{marginTop: '16px', fontSize: '18px'}}>No Frontend Canister Available</h3>
                <p style={{textAlign: 'center', maxWidth: '400px', marginTop: '8px'}}>
                    Assets are stored in a frontend canister, which this project doesn't have yet.
                    Add a frontend canister to your project to manage assets.
                </p>
            </div>
        );
    }

    const Divider = () => (
        <div
            ref={dividerRef}
            style={{
                height: '6px',
                backgroundColor: colorPalette.border,
                cursor: 'ns-resize',
                position: 'relative',
                marginTop: '-3px',
                zIndex: 10,
                opacity: isDraggingDivider ? 0.8 : 0.4,
                transition: 'opacity 0.2s'
            }}
            onMouseDown={(e) => {
                e.preventDefault();
                setIsDraggingDivider(true);
            }}
            onMouseOver={() => dividerRef.current?.style.setProperty('opacity', '0.8')}
            onMouseOut={() => !isDraggingDivider && dividerRef.current?.style.setProperty('opacity', '0.4')}
        >
            <div style={{
                position: 'absolute',
                top: '1px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '4px',
                backgroundColor: colorPalette.accent,
                borderRadius: '2px',
            }}/>
        </div>
    );

    return (
        <div
            style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: colorPalette.background,
                color: colorPalette.foreground,
                overflow: 'hidden'
            }}
            onDragEnter={handleDrag}
        >
            {/* Toolbar */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                borderBottom: `1px solid ${colorPalette.border}`
            }}>
                <div style={{display: 'flex', gap: '8px'}}>
                    <button
                        onClick={triggerFileInput}
                        style={{
                            backgroundColor: colorPalette.button,
                            color: colorPalette.buttonText,
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        Upload
                    </button>
                    {/* TEMPORARY DEBUG BUTTON */}
                    <button
                        onClick={async () => {
                            if (!assetManagerRef.current) return;
                            const assetList = await assetManagerRef.current.list();
                            console.log("=== ALL ASSETS IN CANISTER ===");
                            assetList.forEach(asset => {
                                const key = typeof asset === 'string' ? asset : asset.key;
                                if (key.includes('backend_prompt') || key.includes('backend_rules') || key.includes('frontend_prompt')) {
                                    console.log("Found uploaded file:", key);
                                }
                            });
                            console.log("Total assets:", assetList.length);
                        }}
                        style={{
                            backgroundColor: '#FF6B6B',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer'
                        }}
                    >
                        🔍 Debug
                    </button>
                    <button
                        onClick={() => {
                            const folderName = prompt('Enter folder name');
                            if (folderName) createFolder(folderName);
                        }}
                        style={{
                            backgroundColor: 'transparent',
                            color: colorPalette.foreground,
                            border: `1px solid ${colorPalette.border}`,
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2">
                            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                            <line x1="12" y1="11" x2="12" y2="17"/>
                            <line x1="9" y1="14" x2="15" y2="14"/>
                        </svg>
                        New Folder
                    </button>
                    {selectedAsset && (
                        <button
                            onClick={() => {
                                if (window.confirm(`Are you sure you want to delete "${selectedAsset.fileName}"?`)) {
                                    deleteAsset(selectedAsset);
                                }
                            }}
                            style={{
                                backgroundColor: colorPalette.error,
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                            Delete
                        </button>
                    )}
                </div>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    {/* Toggle for showing all assets */}
                    <button
                        onClick={() => {
                            const newShowAllAssets = !showAllAssets;
                            setShowAllAssets(newShowAllAssets);
                            setCurrentFolder('');
                            setSelectedAsset(null);
                        }}
                        style={{
                            backgroundColor: showAllAssets ? colorPalette.accent : 'transparent',
                            color: showAllAssets ? colorPalette.buttonText : colorPalette.foreground,
                            border: `1px solid ${colorPalette.border}`,
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        title={showAllAssets
                            ? "Project View: View and upload only to project assets"
                            : "All Assets View: View entire canister and upload anywhere"}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                        </svg>
                        {showAllAssets ? "Project View" : "All Assets"}
                    </button>

                    {/* Breadcrumb Navigation */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px'
                    }}>
                        <button
                            onClick={goToProjectRoot}
                            style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: colorPalette.foreground,
                                fontSize: '14px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                                <polyline points="9 22 9 12 15 12 15 22"/>
                            </svg>
                            Project Root
                        </button>

                        {currentFolder.startsWith(`projects/${projectId}/`) && (
                            <>
                                {currentFolder
                                    .substring(`projects/${projectId}/`.length)
                                    .split('/')
                                    .filter(Boolean)
                                    .map((folder, index, array) => (
                                        <React.Fragment key={index}>
                                            <span>/</span>
                                            <button
                                                onClick={() => {
                                                    const basePath = `projects/${projectId}/`;
                                                    const targetPath = array.slice(0, index + 1).join('/');
                                                    setCurrentFolder(`${basePath}${targetPath}`);
                                                    setSelectedAsset(null);
                                                    loadAssets();
                                                }}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    color: index === array.length - 1 ? colorPalette.accent : colorPalette.foreground,
                                                    fontWeight: index === array.length - 1 ? 'bold' : 'normal',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    padding: '4px'
                                                }}
                                            >
                                                {folder}
                                            </button>
                                        </React.Fragment>
                                    ))}
                            </>
                        )}
                    </div>


                    {/* View and Sort Controls */}
                    <div style={{display: 'flex', gap: '4px'}}>
                        <button
                            onClick={() => setView('grid')}
                            style={{
                                backgroundColor: view === 'grid' ? colorPalette.highlight : 'transparent',
                                border: `1px solid ${colorPalette.border}`,
                                borderRadius: '4px',
                                padding: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title="Grid View"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colorPalette.foreground}
                                 strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"/>
                                <rect x="14" y="3" width="7" height="7"/>
                                <rect x="14" y="14" width="7" height="7"/>
                                <rect x="3" y="14" width="7" height="7"/>
                            </svg>
                        </button>
                        <button
                            onClick={() => setView('list')}
                            style={{
                                backgroundColor: view === 'list' ? colorPalette.highlight : 'transparent',
                                border: `1px solid ${colorPalette.border}`,
                                borderRadius: '4px',
                                padding: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title="List View"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colorPalette.foreground}
                                 strokeWidth="2">
                                <line x1="8" y1="6" x2="21" y2="6"/>
                                <line x1="8" y1="12" x2="21" y2="12"/>
                                <line x1="8" y1="18" x2="21" y2="18"/>
                                <line x1="3" y1="6" x2="3.01" y2="6"/>
                                <line x1="3" y1="12" x2="3.01" y2="12"/>
                                <line x1="3" y1="18" x2="3.01" y2="18"/>
                            </svg>
                        </button>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            style={{
                                borderRadius: '4px',
                                border: `1px solid ${colorPalette.border}`,
                                padding: '4px 8px',
                                backgroundColor: 'transparent',
                                color: colorPalette.foreground,
                                fontSize: '14px'
                            }}
                        >
                            <option value="name">Name</option>
                            <option value="type">Type</option>
                            <option value="date">Date</option>
                            <option value="size">Size</option>
                        </select>
                        <button
                            onClick={() => setIsSortAscending(!isSortAscending)}
                            style={{
                                backgroundColor: 'transparent',
                                border: `1px solid ${colorPalette.border}`,
                                borderRadius: '4px',
                                padding: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title={isSortAscending ? "Sort Descending" : "Sort Ascending"}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={colorPalette.foreground}
                                strokeWidth="2"
                                style={{
                                    transform: isSortAscending ? 'none' : 'rotate(180deg)'
                                }}
                            >
                                <line x1="12" y1="19" x2="12" y2="5"/>
                                <polyline points="5 12 12 5 19 12"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Search input */}
            <div style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${colorPalette.border}`
            }}>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search assets..."
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: `1px solid ${colorPalette.border}`,
                        backgroundColor: '#fff',
                        fontSize: '14px'
                    }}
                />
            </div>

            {/* Main content area */}
            <div
                style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '12px',
                    position: 'relative'
                }}
                onDragOver={handleDrag}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
            >
                {/* Loading indicator */}
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(253, 246, 227, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                border: `4px solid ${colorPalette.border}`,
                                borderTopColor: colorPalette.accent,
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <div style={{
                                color: colorPalette.foreground,
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}>Loading assets...
                            </div>
                        </div>
                    </div>
                )}

                {/* Drag overlay */}
                {dragActive && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: colorPalette.dropActive,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 5,
                        border: `2px dashed ${colorPalette.accent}`,
                        borderRadius: '8px'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colorPalette.accent}
                                 strokeWidth="2">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            <span style={{
                                color: colorPalette.accent,
                                fontSize: '18px',
                                fontWeight: 'bold'
                            }}>Drop files here to upload</span>
                        </div>
                    </div>
                )}

                {/* No assets message */}
                {!isLoading && assets.length === 0 && (
                    <div style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke={colorPalette.border}
                             strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                        </svg>
                        <div style={{
                            color: colorPalette.foreground,
                            fontSize: '16px',
                            textAlign: 'center'
                        }}>
                            {currentFolder ? (
                                <>
                                    <p>This folder is empty</p>
                                    <button
                                        onClick={navigateUp}
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: colorPalette.accent,
                                            border: 'none',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            padding: '8px',
                                            marginTop: '8px'
                                        }}
                                    >
                                        Go back to parent folder
                                    </button>
                                </>
                            ) : (
                                <p>No assets found. Upload some files to get started.</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Grid/List View of Assets */}
                {!isLoading && assets.length > 0 && (
                    <>
                        {/* If in a subfolder, show up navigation button */}
                        {currentFolder !== '' && (
                            <div style={{
                                marginBottom: '12px',
                                padding: '8px',
                                backgroundColor: colorPalette.highlight,
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                                 onClick={navigateUp}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                     stroke={colorPalette.foreground} strokeWidth="2">
                                    <path d="M19 12H5"/>
                                    <polyline points="12 19 5 12 12 5"/>
                                </svg>
                                <span>Back to parent folder</span>
                            </div>
                        )}

                        {/* Filtered assets list */}
                        {view === 'grid' ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                gap: '16px',
                                marginTop: '12px'
                            }}>
                                {filteredAssets.map((asset, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: selectedAsset?.key === asset.key ? colorPalette.highlight :
                                                (asset.isFolder && hoveredAsset?.key === asset.key ? colorPalette.folderHover : 'transparent'),
                                            borderRadius: '4px',
                                            padding: '8px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onClick={() => handleAssetClick(asset)}
                                        onDoubleClick={() => handleAssetDoubleClick(asset)}
                                        onContextMenu={(e) => handleContextMenu(e, asset)}
                                        onMouseEnter={() => setHoveredAsset(asset)}
                                        onMouseLeave={() => setHoveredAsset(null)}
                                    >
                                        <div style={{
                                            width: '64px',
                                            height: '64px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            borderRadius: '4px',
                                            backgroundColor: '#fff',
                                            border: `1px solid ${colorPalette.border}`
                                        }}>
                                            {asset.thumbnailUrl ? (
                                                <img
                                                    src={asset.thumbnailUrl}
                                                    alt={asset.fileName}
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            ) : (
                                                renderFileIcon(asset)
                                            )}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            textAlign: 'center',
                                            wordBreak: 'break-word',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            width: '100%',
                                            whiteSpace: 'nowrap'
                                        }} title={asset.fileName}>
                                            {asset.fileName}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                marginTop: '12px'
                            }}>
                                {assets
                                    .filter(asset =>
                                        asset.fileName.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .map((asset, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                backgroundColor: selectedAsset?.key === asset.key ? colorPalette.highlight :
                                                    (asset.isFolder && hoveredAsset?.key === asset.key ? colorPalette.folderHover : 'transparent'),
                                                borderRadius: '4px',
                                                padding: '8px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}
                                            onClick={() => handleAssetClick(asset)}
                                            onDoubleClick={() => handleAssetDoubleClick(asset)}
                                            onContextMenu={(e) => handleContextMenu(e, asset)}
                                            onMouseEnter={() => setHoveredAsset(asset)}
                                            onMouseLeave={() => setHoveredAsset(null)}
                                        >
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {renderFileIcon(asset)}
                                            </div>
                                            <div style={{
                                                flex: 1,
                                                fontSize: '14px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {asset.fileName}
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: colorPalette.foreground + '99',
                                                minWidth: '100px'
                                            }}>
                                                {asset.isFolder ? 'Folder' : asset.contentType}
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: colorPalette.foreground + '99',
                                                minWidth: '80px',
                                                textAlign: 'right'
                                            }}>
                                                {asset.isFolder ? '--' : formatFileSize(asset.size)}
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Context Menu for Folders */}
            {contextMenu.visible && contextMenu.asset && (
                <div
                    style={{
                        position: 'fixed',
                        top: `${contextMenu.y}px`,
                        left: `${contextMenu.x}px`,
                        backgroundColor: '#fff',
                        border: `1px solid ${colorPalette.border}`,
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '120px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={() => {
                            if (contextMenu.asset && window.confirm(`Are you sure you want to delete the folder "${contextMenu.asset.fileName}" and all its contents?`)) {
                                deleteAsset(contextMenu.asset);
                            }
                            closeContextMenu();
                        }}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: colorPalette.error,
                            fontSize: '14px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colorPalette.highlight;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Delete Folder
                    </button>
                </div>
            )}

            {/* Selected asset details panel */}
            {selectedAsset && !selectedAsset.isFolder && (
                <>
                    <Divider/>
                    <div style={{
                        borderTop: `1px solid ${colorPalette.border}`,
                        padding: '16px',
                        backgroundColor: '#fff',
                        height: `${detailsPanelHeight}px`,
                        overflow: 'auto',
                        display: 'flex',
                        gap: '16px',
                        position: 'relative'
                    }}>
                        <div style={{
                            maxWidth: '40%',
                            overflow: 'hidden'
                        }}>
                            {renderAssetPreview()}
                        </div>
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            <div>
                                <h3 style={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    margin: '0 0 4px 0',
                                    color: colorPalette.foreground
                                }}>
                                    {selectedAsset.fileName}
                                </h3>
                                <div style={{
                                    fontSize: '14px',
                                    color: colorPalette.foreground + '99'
                                }}>
                                    {selectedAsset.contentType} • {formatFileSize(selectedAsset.size)}
                                </div>
                            </div>

                            <div style={{
                                fontSize: '14px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }}>
                                <div>
                                    <div style={{fontWeight: 'bold', marginBottom: '4px'}}>Asset URL:</div>
                                    <div style={{
                                        padding: '8px',
                                        backgroundColor: colorPalette.highlight,
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        wordBreak: 'break-all'
                                    }}>
                                        {selectedAsset.url}
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    gap: '8px'
                                }}>
                                    <button
                                        onClick={copyAssetUrl}
                                        style={{
                                            backgroundColor: colorPalette.button,
                                            color: colorPalette.buttonText,
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            flex: 1
                                        }}
                                    >
                                        Copy URL
                                    </button>
                                    <button
                                        onClick={insertAssetUrlIntoEditor}
                                        style={{
                                            backgroundColor: colorPalette.accent,
                                            color: colorPalette.buttonText,
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            flex: 1
                                        }}
                                    >
                                        Insert in Editor
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Close button */}
                        <button
                            onClick={() => setSelectedAsset(null)}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                                borderRadius: '4px'
                            }}
                            title="Close preview"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colorPalette.foreground}
                                 strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </>
            )}

            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{display: 'none'}}
                onChange={handleFileInputChange}
                multiple
            />

            {/* CSS for animations */}
            <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default AssetManagerPanel;